// IST timezone helpers. The team operates in India, so all date keys
// (YYYY-MM-DD for Firestore queries, RTDB attendance keys, dailyWork keys)
// MUST be computed in IST to avoid off-by-one bugs around midnight UTC.

const IST_OFFSET_MINUTES = 330

function toIST(d: Date): Date {
  return new Date(d.getTime() + IST_OFFSET_MINUTES * 60 * 1000)
}

export function todayIST(): string {
  const ist = toIST(new Date())
  return ist.toISOString().slice(0, 10)
}

export function nowIST(): Date {
  return toIST(new Date())
}

export function formatISTDate(d: Date | string | number): string {
  const date = typeof d === 'string' || typeof d === 'number' ? new Date(d) : d
  if (isNaN(date.getTime())) return ''
  return toIST(date).toISOString().slice(0, 10)
}

export function istHourMinute(): { hour: number; minute: number } {
  const ist = toIST(new Date())
  return { hour: ist.getUTCHours(), minute: ist.getUTCMinutes() }
}

// Convert any HH:MM "wall-clock" string to a minute count safely.
export function timeToMinutes(t: string | null | undefined): number | null {
  if (!t || typeof t !== 'string') return null
  const m = t.match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null
  if (h < 0 || h > 23 || min < 0 || min > 59) return null
  return h * 60 + min
}

export function isValidYMD(s: unknown): s is string {
  return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)
}

// Inclusive day diff between two YYYY-MM-DD strings; safe across DST.
// Uses UTC anchor at noon so neither end is shifted by spring-forward/fall-back.
export function inclusiveDaysBetween(start: string, end: string): number {
  if (!isValidYMD(start) || !isValidYMD(end)) return 0
  const s = Date.UTC(+start.slice(0, 4), +start.slice(5, 7) - 1, +start.slice(8, 10), 12)
  const e = Date.UTC(+end.slice(0, 4), +end.slice(5, 7) - 1, +end.slice(8, 10), 12)
  if (e < s) return 0
  return Math.round((e - s) / 86_400_000) + 1
}

export function isWeekendYMD(date: string): boolean {
  if (!isValidYMD(date)) return false
  const d = new Date(date + 'T00:00:00Z')
  // Sunday is the only weekly off. Saturday is a regular working day — it must
  // show attendance/events on the calendar and count as a working day.
  return d.getUTCDay() === 0
}

// Working-day count between two YYYY-MM-DD strings (inclusive), excluding
// weekends (Sundays only) and any dates in holidaySet.
export function workingDaysBetween(start: string, end: string, holidaySet?: Set<string>): number {
  if (!isValidYMD(start) || !isValidYMD(end)) return 0
  const s = new Date(start + 'T00:00:00Z')
  const e = new Date(end + 'T00:00:00Z')
  if (e < s) return 0
  let count = 0
  for (let d = s; d <= e; d = new Date(d.getTime() + 86_400_000)) {
    if (isWeekendYMD(d.toISOString().slice(0, 10))) continue
    const key = d.toISOString().slice(0, 10)
    if (holidaySet?.has(key)) continue
    count++
  }
  return count
}
